import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { Chart as ChartJS } from 'chart.js'
import intersection from 'lodash/intersection'
import cloneDeep from 'lodash/cloneDeep'
import pica from 'pica'
import {
  Container,
  Header,
  Title,
  Content,
  ChartArea,
  PropertiesContainer,
  PropertiesTitle,
  PropertiesDate,
  PropertiesList,
  Footer,
  SuccessfulCopyMessage,
  ContainerLoading,
  EmptyMessage,
  PropertiesContainerHeader,
  PropertyTitle,
  ErrorContainer
} from './styles'
import { PropertiesComparativeChart } from './PropertiesComparativeChart'
import { CalendarFilter } from './calendarFilter'
import { QuantitativeProperty } from '../property/QuantitativeProperty'
import { MaxPropertiesSelectedMessage } from '../MaxPropertiesSelectedMessage'
import { IconComponent } from '~/assets/icons/component'
import { HealthProcedureDetails } from '~/components/healthProcedures/healthProcedureDetails'
import { hasObjectValue, logError, resetTimePartOfDateToEndOfDay, toFalse, toTrue, transformDate } from '~/utils'
import { ELaboratoryTestPropertyTypes, ILaboratoryTestProperty, LaboratoryTestQuantitativeProperty } from '~/interfaces'
import { fetchQuantitativePropertyMeasurementHistory } from '~/services'
import { useLaboratoryTest } from '~/context'
import { DataNotFound, SpinnerIconLoader } from '~/components/shared'
import { FIVE_SECONDS, MAX_PROPERTIES_SELECTED } from '~/constants'
import { CopyButton } from '~/components/shared/CopyButton'
import { ExportButton } from '~/components/shared/ExportButton'
import { theme } from '~/styles'

interface Props {
  patientId: number
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
}

interface IMeasurement {
  id: number
  name: string
  history: LaboratoryTestQuantitativeProperty[]
}

export const LaboratoryTestPropertiesComparison: React.FC<Props> = ({
  showModal,
  setShowModal,
  patientId
}: Props): JSX.Element => {
  const [isLoadingChart, setIsLoadingChart] = useState<boolean>(true)
  const startLoadingChart = () => setIsLoadingChart(true)
  const stopLoadingChart = () => setIsLoadingChart(false)

  const [datasets, setDatasets] = useState<any>({ labels: [], datasets: [] })
  const [options, setOptions] = useState<any>({})
  const [measurements, setMeasurements] = useState<IMeasurement[]>([])
  const [unexpectedError, setUnexpectedError] = useState<boolean>(false)

  const [filterPeriod, setFilterPeriod] = useState<Date[]>([])

  const [wasChartImageCopied, setWasChartImageCopied] = useState<boolean>(false)

  const { laboratoryTest, laboratoryTestProperties, selectedProperties, setSelectedProperties } = useLaboratoryTest()

  const refChart = useRef<ChartJS<'line', number[], string> | undefined>(null)

  const colors = [
    theme.colors.colorF76337,
    theme.colors.color37D9F7,
    theme.colors.colorB7CC10,
    theme.colors.color1060CC,
    theme.colors.color37F737,
    theme.colors.colorF7DD37
  ]

  const disableChartInteractionButtons = selectedProperties.length <= 0 || datasets?.datasets.length <= 0
  const maximumAmountOfSelectedPropertiesHasBeenNotReached = selectedProperties.length < MAX_PROPERTIES_SELECTED

  const getFetchQuantitativePropertyMeasurementHistoryPromise = async ({
    patientId,
    propertyCodes,
    sample
  }: {
    patientId: number
    propertyCodes: number[]
    sample: string
  }) => {
    try {
      return await Promise.resolve(
        fetchQuantitativePropertyMeasurementHistory({
          pathParams: { patientId },
          queryParams: { propertyCodes, sample, type: ELaboratoryTestPropertyTypes.QUANTITATIVE }
        })
      )
    } catch (error) {
      stopLoadingChart()
      setUnexpectedError(true)
      return []
    }
  }

  useEffect(() => {
    let ignore = false

    const fetchHistoryToSelectedProperties = async () => {
      startLoadingChart()
      let fetchPromises: Array<Promise<ILaboratoryTestProperty[]>> = []

      selectedProperties.forEach(selectedProperty => {
        const propertyQuantitative = laboratoryTestProperties.find(property => {
          const isQuantitative = property.type === ELaboratoryTestPropertyTypes.QUANTITATIVE
          return property.code === selectedProperty && isQuantitative
        })

        if (propertyQuantitative) {
          fetchPromises = [
            ...fetchPromises,
            getFetchQuantitativePropertyMeasurementHistoryPromise({
              patientId,
              propertyCodes: [selectedProperty],
              sample: propertyQuantitative.sample
            })
          ]
        }
      })

      try {
        const histories = await Promise.all([...fetchPromises])

        let aaa: any[] = []
        histories.forEach(h => {
          aaa = [...aaa, ...addMeasurement(h)]
        })

        if (!ignore) {
          setMeasurements([...aaa])
        }
      } catch (error) {
        setMeasurements([])
        setSelectedProperties([])
      }
    }

    fetchHistoryToSelectedProperties()

    return () => {
      ignore = true
    }
  }, [])

  useEffect(() => {
    if (!showModal) {
      setMeasurements([])
    }
  }, [showModal])

  useEffect(() => {
    if (!wasChartImageCopied) {
      return
    }

    const timer = setTimeout(() => {
      setWasChartImageCopied(toFalse)
    }, FIVE_SECONDS)

    return () => clearTimeout(timer)
  }, [wasChartImageCopied])

  const formatMeasurementsForChart = (newMeasurements: IMeasurement[]) => {
    const areThereMeasurements = newMeasurements.length > 0
    if (areThereMeasurements) {
      const copyMeasurements = cloneDeep(newMeasurements)

      let labelsUpdated: any[] = [] // eixo x
      let datasetsUpdated: any[] = [] // eixo y
      let scalesUpdated: any = null

      if (copyMeasurements.length > 1) {
        // get intersection dates
        const intersectionDates = intersection(...copyMeasurements.map(m => m.history.map(h => h.date)))

        // filter measurements that are in the intersection
        copyMeasurements.forEach(m => (m.history = m.history.filter(h => intersectionDates.includes(h.date))))

        labelsUpdated = copyMeasurements[0].history.map(({ date }) => transformDate({ date }))

        copyMeasurements.forEach((m, index) => {
          datasetsUpdated = [
            ...datasetsUpdated,
            {
              yAxisID: `yAxis-${index}`,
              label: m.name,
              data: [...m.history.map(({ value }) => value)],
              borderColor: colors[index],
              backgroundColor: colors[index],
              pointRadius: 4,
              pointHoverRadius: 6,
              pointHitRadius: 20
            }
          ]

          scalesUpdated = {
            ...scalesUpdated,
            [`yAxis-${index}`]: {
              type: 'linear',
              position: 'left',
              beginAtZero: true,
              ticks: {
                color: colors[index]
              }
            }
          }
        })

        stopLoadingChart()
      } else {
        labelsUpdated = copyMeasurements[0].history.map(({ date }) => transformDate({ date }))

        datasetsUpdated = [
          {
            yAxisID: 'yAxis-0',
            label: copyMeasurements[0].name,
            data: [...copyMeasurements[0].history.map(({ value }) => value)],
            borderColor: colors[0],
            backgroundColor: colors[0],
            pointRadius: 4,
            pointHoverRadius: 6,
            pointHitRadius: 20
          }
        ]

        scalesUpdated = {
          'yAxis-0': {
            type: 'linear',
            position: 'left',
            beginAtZero: true,
            ticks: {
              color: colors[0]
            }
          }
        }

        stopLoadingChart()
      }

      setDatasets({ labels: labelsUpdated, datasets: datasetsUpdated })

      const laboratoryTestDate = transformDate({ date: laboratoryTest.startDate })
      const hasLaboratoryTestDate = labelsUpdated.includes(laboratoryTestDate)

      if (hasLaboratoryTestDate) {
        setOptions({
          plugins: {
            annotation: {
              annotations: {
                dataProcedimento: {
                  type: 'line',
                  mode: 'vertical',
                  borderColor: 'black',
                  borderWidth: 2,
                  scaleID: 'x',
                  value: transformDate({ date: laboratoryTest.startDate })
                }
              }
            }
          },
          scales: scalesUpdated
        })
      } else {
        setOptions({ scales: [], plugins: {} })
      }
    } else {
      setDatasets({ labels: [], datasets: [] })
      setOptions({ scales: [] })
    }
  }

  const filterMeasurementsPerDate = (measurements: IMeasurement[], rangeDates: Date[]): IMeasurement[] => {
    const [startDate, endDate] = rangeDates
    const startDateTimestamp = startDate.getTime()
    const endDateTimestamp = endDate.getTime()

    const filterMeasurementsDate = measurements.flatMap(measurement => {
      const measurementExams = measurement.history.filter(history => {
        const historyTimestamp = new Date(history.date).getTime()
        return historyTimestamp >= startDateTimestamp && historyTimestamp <= endDateTimestamp
      })

      return measurementExams.length > 0 ? { ...measurement, history: measurementExams } : []
    })

    return filterMeasurementsDate
  }

  useEffect(() => {
    if (filterPeriod.length > 0) {
      const filteredMeasurements = filterMeasurementsPerDate(measurements, filterPeriod)
      formatMeasurementsForChart(filteredMeasurements)
    } else {
      formatMeasurementsForChart(measurements)
    }
  }, [measurements, filterPeriod])

  const handleOnChange = (property: { name: string; isSelected: boolean; id: number; sample: string }) => {
    const { sample, isSelected, id } = property
    setUnexpectedError(false)
    if (isSelected && maximumAmountOfSelectedPropertiesHasBeenNotReached) {
      startLoadingChart()
      setSelectedProperties(prev => [...prev, id])
      fetchQuantitativePropertyMeasurementHistory({
        pathParams: { patientId },
        queryParams: { propertyCodes: [id], sample }
      })
        .then(h => setMeasurements([...addMeasurement(h)]))
        .catch(error => {
          setSelectedProperties(prev => prev.filter(p => p !== id))
          setUnexpectedError(true)
          setIsLoadingChart(false)
          logError(error)
        })
    }

    const isNotSelected = !isSelected
    if (isNotSelected) {
      setSelectedProperties(prev => prev.filter(p => p !== id))
      setMeasurements(prev => prev.filter(m => m.id !== id))
    }
  }

  const addMeasurement = (propertyMeasurementHistory: any): any[] => {
    // remove part time from date measurement
    const copyHistory = [
      ...propertyMeasurementHistory.map(m => ({ ...m, date: resetTimePartOfDateToEndOfDay(m.date) }))
    ]

    // sort by date ascending
    copyHistory.sort((h1, h2) => new Date(h1.date).getTime() - new Date(h2.date).getTime())

    const measurementsUpdated = cloneDeep(measurements)
    measurementsUpdated.push({
      id: copyHistory[0].code,
      name: copyHistory[0].name,
      history: [...copyHistory]
    })

    return measurementsUpdated
  }

  const getChartImageAsBase64 = (chart: React.RefObject<ChartJS<'line', number[], string> | undefined>): string => {
    return chart?.current?.toBase64Image ? chart.current.toBase64Image?.() : ''
  }

  const copyChartImageToClipboardFrom = (imageAsBase64: string): void => {
    const base64ToBlob = async (base64: string): Promise<Blob> =>
      await new Promise((resolve, reject) => {
        fetch(base64)
          .then(response => resolve(response.blob()))
          .catch(reject)
      })

    const blobToClipboard = async (blob: Blob): Promise<void> =>
      await new Promise((resolve, reject) => {
        navigator.clipboard
          .write([new ClipboardItem({ [blob.type]: blob })])
          .then(() => resolve())
          .catch(reject)
      })

    const setImageWasCopiedToTrue = (): void => setWasChartImageCopied(toTrue)

    base64ToBlob(imageAsBase64).then(blobToClipboard).then(setImageWasCopiedToTrue).catch(logError)
  }

  function generateStamp(imageContent: HTMLImageElement) {
    const formattedDate = transformDate({
      date: new Date(),
      dateFormat: "dd/MM/yyyy 'às' HH:mm",
      withHourAndMinute: true
    })
    const stamp = `Gerado pelo sistema H.Story no dia ${formattedDate}`
    const stampHeightInPixels = 20
    const stampWidthInPixels = 320
    const canvas = document.createElement('canvas')

    canvas.width = imageContent.width
    canvas.height = imageContent.height + stampHeightInPixels

    const context = canvas.getContext('2d')
    if (context) {
      context.fillStyle = 'white'
      context.fillRect(0, 0, canvas.width, canvas.height)

      context.drawImage(imageContent, 0, 0)

      context.font = '12px Arial'
      context.fillStyle = 'rgba(0, 0, 0, 0.5)'
      context.fillText(stamp, canvas.width - stampWidthInPixels, canvas.height - 5)
    }

    return canvas
  }

  function resizeImagePica(base64Image: string, callback: (base64Image: string) => void) {
    const resizedWidth = 680
    let resizedHeight = 0

    const img = new Image()
    img.src = base64Image

    img.onload = () => {
      if (img.height <= resizedHeight && img.width <= resizedWidth) {
        callback(base64Image)
      } else {
        resizedHeight = Math.floor((resizedWidth * img.height) / img.width)

        const resizedCanvas = document.createElement('canvas')
        const stampHeightInPixels = 20
        resizedCanvas.width = resizedWidth
        resizedCanvas.height = resizedHeight + stampHeightInPixels

        pica()
          .resize(generateStamp(img), resizedCanvas, {
            quality: 3,
            unsharpAmount: 100,
            unsharpRadius: 0.6,
            unsharpThreshold: 2
          })
          .then(result => {
            callback(result.toDataURL('image/png', 1))
          })
      }
    }
  }

  const handleCopyChartImage = () => {
    if (refChart) {
      const imageAsBase64 = getChartImageAsBase64(refChart)
      resizeImagePica(imageAsBase64, copyChartImageToClipboardFrom)
    }
  }

  const exportChartImageToDownloadFrom = (imageAsBase64: string): void => {
    const downloadLink = document.createElement('a')
    downloadLink.href = imageAsBase64
    downloadLink.download = 'grafico-comparativo-exame-laboratorial.png'
    downloadLink.click()
  }

  const handleExportChartImage = () => {
    if (refChart?.current) {
      const imageAsBase64 = getChartImageAsBase64(refChart)

      resizeImagePica(imageAsBase64, exportChartImageToDownloadFrom)
    }
  }

  const handleFilterByDate = (rangeDates: Date[]): void => {
    if (rangeDates.length !== 1) {
      setFilterPeriod(rangeDates)
    }
  }

  return (
    <HealthProcedureDetails showModal={showModal} setShowModal={setShowModal} viewType="BigModal">
      {hasObjectValue({ ...laboratoryTest }) && (
        <Container>
          <Header>
            <Title>{laboratoryTest?.description}</Title>
          </Header>
          <Content>
            <ChartArea>
              {unexpectedError ? (
                <ErrorContainer>
                  <DataNotFound text="Ocorreu um erro inesperado" />
                </ErrorContainer>
              ) : (
                <>
                  {datasets?.datasets.length <= 0 && !isLoadingChart && (
                    <EmptyMessage>Não há medições para o período selecionado.</EmptyMessage>
                  )}
                  {datasets?.datasets.length > 0 && (
                    <PropertiesComparativeChart data={datasets} options={options} ref={refChart} />
                  )}
                </>
              )}
            </ChartArea>

            <PropertiesContainer>
              <PropertiesContainerHeader>
                <div>
                  <PropertiesTitle>Itens do Exame</PropertiesTitle>
                  <PropertiesDate>
                    {transformDate({ date: laboratoryTest.startDate, withHourAndMinute: true })}
                  </PropertiesDate>
                </div>
                <CalendarFilter filterPeriod={filterPeriod} handleFilterByDate={handleFilterByDate} />
              </PropertiesContainerHeader>
              <MaxPropertiesSelectedMessage
                type={maximumAmountOfSelectedPropertiesHasBeenNotReached ? 'info' : 'warning'}
              />
              <PropertiesList>
                {laboratoryTestProperties.map((property, index, propertyArray) => {
                  const isQuantitativeProperty = property.type === ELaboratoryTestPropertyTypes.QUANTITATIVE
                  const isTitle = property.type === ELaboratoryTestPropertyTypes.SECTION
                  const nextProperty = propertyArray[index + 1]
                  const theSectionHasProperties =
                    nextProperty && nextProperty.type !== ELaboratoryTestPropertyTypes.SECTION

                  if (isTitle && theSectionHasProperties) {
                    const { title } = property
                    return (
                      <React.Fragment key={title}>
                        <PropertyTitle>{title}</PropertyTitle>
                        <hr />
                      </React.Fragment>
                    )
                  }

                  if (isQuantitativeProperty) {
                    const {
                      name,
                      code,
                      sample,
                      prefix,
                      value,
                      lowerReferenceValue,
                      upperReferenceValue,
                      interpretation,
                      unit
                    } = property
                    return (
                      <QuantitativeProperty
                        key={`${name}-${value}`}
                        id={code}
                        name={name}
                        sample={sample}
                        prefix={prefix}
                        value={value}
                        unit={unit}
                        lowerReferenceValue={lowerReferenceValue}
                        upperReferenceValue={upperReferenceValue}
                        interpretation={interpretation}
                        onChange={handleOnChange}
                        selected={selectedProperties.includes(code)}
                      />
                    )
                  }
                })}
              </PropertiesList>
            </PropertiesContainer>

            {isLoadingChart ? (
              <ContainerLoading aria-hidden={true}>
                <SpinnerIconLoader iconComponent={<IconComponent name={'loading'} />} size={75} velocity={2} />
              </ContainerLoading>
            ) : null}
          </Content>
          <Footer>
            <ExportButton onClick={handleExportChartImage} disabled={disableChartInteractionButtons} />
            <CopyButton
              onClick={handleCopyChartImage}
              wasCopied={wasChartImageCopied}
              disabled={disableChartInteractionButtons}
            />
            {wasChartImageCopied && <SuccessfulCopyMessage>Copiado para a área de transferência</SuccessfulCopyMessage>}
          </Footer>
        </Container>
      )}
    </HealthProcedureDetails>
  )
}
